import React, { useState, createContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import useSiteData from '../../hooks/useSiteData';
import {
  buildActiveFeatureSet,
  buildFeaturesFromSiteFeatureControls,
  queryStringContainsDebugFlag,
} from '../../featureSelection/featureSelection';

const UserFeatureContext = createContext(null);

const UserFeatureProvider = props => {
  const { children } = props;

  let search;
  if (typeof window !== `undefined`) {
    search = window.location?.search;
  }

  const { featureControls } = useSiteData();

  const [debugEnabled, setDebugEnabled] = useState(false);
  const [siteConfigurationFeatures, setSiteConfigurationFeatures] = useState(
    []
  );
  const [debugFeatureOverrides, setDebugFeatureOverrides] = useState([]);
  const [activeFeatures, setActiveFeatures] = useState([]);

  useEffect(() => {
    setDebugEnabled(queryStringContainsDebugFlag(search));
  }, [search]);

  useEffect(() => {
    setSiteConfigurationFeatures(
      buildFeaturesFromSiteFeatureControls(featureControls)
    );
  }, [featureControls]);

  useEffect(
    () =>
      setActiveFeatures(
        buildActiveFeatureSet({
          siteConfigurationFeatures,
          debugFeatureOverrides,
        })
      ),
    [siteConfigurationFeatures, debugFeatureOverrides]
  );

  function overrideFeatureSettings({ featureName }) {
    const debugFeatureOverridesClone = [...debugFeatureOverrides];
    const overrideFeatureIndex = debugFeatureOverridesClone.findIndex(
      feature => feature.featureName === featureName
    );

    // feature override already exists, just toggle the enabled flag
    if (overrideFeatureIndex > -1) {
      debugFeatureOverridesClone[overrideFeatureIndex].enabled =
        !debugFeatureOverridesClone[overrideFeatureIndex].enabled;
      setDebugFeatureOverrides(debugFeatureOverridesClone);
      return;
    }

    // feature override does not exist, see if feature exists from site configuration feature controls
    const siteConfigFeatureIndex = siteConfigurationFeatures.findIndex(
      siteFeature => siteFeature.featureName === featureName
    );
    // feature exists in site config feature controls
    if (siteConfigFeatureIndex > -1) {
      debugFeatureOverridesClone.push({
        featureName,
        enabled: !siteConfigurationFeatures[siteConfigFeatureIndex].enabled,
      });
      setDebugFeatureOverrides(debugFeatureOverridesClone);
      return;
    }

    // feature does not exist as override or site feature, add it with default enablement of true.
    debugFeatureOverridesClone.push({
      featureName,
      enabled: true,
    });
    setDebugFeatureOverrides(debugFeatureOverridesClone);
  }

  return (
    <UserFeatureContext.Provider
      value={{
        debugEnabled,
        setDebugEnabled,
        siteConfigurationFeatures,
        debugFeatureOverrides,
        overrideFeatureSettings,
        activeFeatures,
      }}
    >
      {children}
    </UserFeatureContext.Provider>
  );
};

UserFeatureProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export { UserFeatureContext, UserFeatureProvider };
